import { useEffect, useMemo, useState } from 'react';
import { fetchSyncStatus, type SyncStatus, type TimeOffEntry } from '../../../../api/timeOff';
import { buildTimeOffSyncNotice, type TimeOffSyncNoticeTone } from '../timeOffSyncNotice';

interface TimeOffSyncStatusBannerProps {
  entries?: TimeOffEntry[];
  /** Pill (inline toolbar) or full-width alert bar for mobile. */
  variant?: 'pill' | 'alert';
  /** When set, only render for matching sync notice tone. */
  onlyTone?: TimeOffSyncNoticeTone;
  /** Shortens copy for cramped toolbars while keeping the full message as the title. */
  compact?: boolean;
}

const toneStyles = {
  warning: {
    background: 'var(--ops-status-warning-bg, #fef3c7)',
    border: '1px solid var(--ops-status-warning-border, #fcd34d)',
    color: 'var(--ops-status-warning-fg, #92400e)'
  },
  danger: {
    background: 'var(--ops-status-danger-bg, #fee2e2)',
    border: '1px solid var(--ops-status-danger-border, #fca5a5)',
    color: 'var(--ops-status-danger-fg, #991b1b)'
  }
} as const;

export function TimeOffSyncStatusBanner({
  entries = [],
  variant = 'pill',
  onlyTone,
  compact = false
}: TimeOffSyncStatusBannerProps) {
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchSyncStatus()
      .then((status) => {
        if (!cancelled) setSyncStatus(status);
      })
      .catch(() => {
        if (!cancelled) setSyncStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, [entries]);

  const notice = useMemo(() => buildTimeOffSyncNotice(syncStatus, entries), [entries, syncStatus]);
  if (!notice) return null;
  if (onlyTone && notice.tone !== onlyTone) return null;

  const palette = toneStyles[notice.tone];
  const isAlert = variant === 'alert';
  const message = compact && !isAlert ? compactSyncNoticeMessage(notice.message) : notice.message;

  return (
    <div
      role="status"
      className={`time-off-sync-status-banner${isAlert ? ' time-off-sync-status-banner--alert' : ''}`}
      title={compact ? notice.message : undefined}
      style={{
        display: isAlert ? 'flex' : 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
        padding: isAlert ? '0.5rem 0.75rem' : '0.25rem 0.625rem',
        borderRadius: isAlert ? 8 : 999,
        width: isAlert ? '100%' : undefined,
        background: palette.background,
        border: palette.border,
        color: palette.color,
        fontSize: 12,
        fontWeight: 500
      }}
    >
      <span aria-hidden style={{ fontSize: 14 }}>
        {notice.tone === 'danger' ? '\u26A0' : '\u21BB'}
      </span>
      <span>{message}</span>
    </div>
  );
}

function compactSyncNoticeMessage(message: string): string {
  const count = message.match(/^\d+/)?.[0];
  if (message.includes('calendar sync errors') || message.includes('Last calendar sync reported')) {
    return count ? `${count} sync error${count === '1' ? '' : 's'}` : 'Sync error';
  }
  if (message.includes('waiting to sync')) {
    return count ? `${count} waiting to sync` : 'Waiting to sync';
  }
  if (message.includes('employee assigned')) {
    return count ? `${count} need employee` : 'Needs employee';
  }
  if (message.includes('no calendar target')) {
    return count ? `${count} no target` : 'No target';
  }
  return message;
}
